import STRIPE_CURRENCIES from '@stripe-internal/currencies';

// ISO 4217 currencies — have a numeric code. Suitable as the default allowlist for fiat use.
export const ISO_CURRENCY_CODES: string[] = Object.keys(STRIPE_CURRENCIES)
  .filter((code) => STRIPE_CURRENCIES[code]?.iso_numeric !== '')
  .map((code) => code.toUpperCase())
  .sort();

// Non-ISO currencies — no ISO 4217 numeric code (crypto, stablecoins, and a few regional currencies).
export const NON_ISO_CURRENCY_CODES: string[] = Object.keys(STRIPE_CURRENCIES)
  .filter((code) => STRIPE_CURRENCIES[code]?.iso_numeric === '')
  .map((code) => code.toUpperCase())
  .sort();

// BGN (Bulgarian Lev) has been deprecated in favor of EUR.
// See: https://incident-reporting.corp.stripe.com/wf/incidents/chance-apparel
const DEPRECATED_CURRENCY_CODES = new Set(['BGN']);

export const normalizeForSearch = (str: string): string =>
  str.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const devWarn = (string: string) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(string);
  }
};

export const createCurrencyFormatter = (
  locale: string,
  currency: string,
  options: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>,
): Intl.NumberFormat => {
  try {
    return new Intl.NumberFormat(locale, {
      ...options,
      style: 'currency',
      currency,
    });
  } catch {
    devWarn(
      `CurrencyField: "${currency}" is not supported by Intl.NumberFormat. Falling back to decimal formatting.`,
    );

    return new Intl.NumberFormat(locale, {
      ...options,
      style: 'decimal',
    });
  }
};

export const getCurrencySymbolAndPosition = (
  locale: string,
  currency: string,
  currencyDisplay: Intl.NumberFormatOptions['currencyDisplay'],
): {symbol: string; symbolPosition: 'start' | 'end'} => {
  const parts = createCurrencyFormatter(locale, currency, {
    minimumFractionDigits: 2,
    currencyDisplay,
  }).formatToParts(1234567890);
  const currencyPart = parts.find((part) => part.type === 'currency');

  if (currencyPart) {
    return {
      symbol: currencyPart.value,
      symbolPosition: parts[0]?.value === currencyPart.value ? 'start' : 'end',
    };
  }

  // Intl.NumberFormat doesn't recognize `currency` (e.g. non-ISO codes like
  // crypto/stablecoins) — fall back to STRIPE_CURRENCIES for the symbol/name.
  const entry = STRIPE_CURRENCIES[currency.toLowerCase()];
  let symbol: string = entry?.symbol || currency.toUpperCase();

  if (currencyDisplay === 'name') {
    symbol = entry?.name ?? currency;
  } else if (currencyDisplay === 'code') {
    symbol = currency.toUpperCase();
  }

  // Symbol placement depends on locale + currencyDisplay, not the specific currency.
  // Use USD as a proxy to get the correct position for non-ISO currencies.
  const proxyParts = createCurrencyFormatter(locale, 'USD', {
    currencyDisplay,
  }).formatToParts(1);
  const symbolPosition = proxyParts[0]?.type === 'currency' ? 'start' : 'end';

  return {symbol, symbolPosition};
};

export const getCurrencyNameFallback = (code: string): string | undefined =>
  STRIPE_CURRENCIES[code.toLowerCase()]?.name;

export const matchesCurrencySearch = (
  inputValue: string,
  candidates: Array<string | null | undefined>,
): boolean => {
  const input = normalizeForSearch(inputValue);
  return candidates.some(
    (candidate) =>
      candidate != null && normalizeForSearch(candidate).includes(input),
  );
};

export const toDisplayValue = (
  normalizedValue: number,
  denominationMultiple: number,
): number => normalizedValue / denominationMultiple;

export const fromDisplayValue = (
  displayValue: number,
  denominationMultiple: number,
  fractionDigitMultiple: number,
): number => {
  const scaled = displayValue * denominationMultiple;
  return (
    Math.round((scaled + Number.EPSILON) * fractionDigitMultiple) /
    fractionDigitMultiple
  );
};

export const getInitialNumericValue = (
  fractionDigitOffset: number,
  defaultValue: number | undefined,
  value: number | null | undefined,
): number | undefined => {
  const doesSupportDecimals = fractionDigitOffset > 0;

  if (doesSupportDecimals) {
    if (typeof defaultValue === 'number') {
      return defaultValue;
    }

    if (typeof value === 'number') {
      return value;
    }

    return undefined;
  }

  if (Number.isInteger(defaultValue)) {
    return defaultValue as number;
  }

  if (Number.isInteger(value)) {
    return value as number;
  }

  return undefined;
};

export const getEffectiveControlledValue = (
  valueIsControlled: boolean,
  value: number | null | undefined,
  doesSupportDecimals: boolean,
): number | null | undefined => {
  if (!valueIsControlled) {
    return undefined;
  }

  if (value == null) {
    return null;
  }

  if (!doesSupportDecimals && !Number.isInteger(value)) {
    // Invalid precision for this currency: treat as controlled-but-empty so
    // the hook stays in controlled mode rather than flipping to uncontrolled.
    return null;
  }

  return value;
};

export const buildCurrencySelectorData = (
  currencyAllowlist: string[],
  internalCurrency: string,
): {
  items: Array<{id: string; label: string}>;
  disabledItems: string[];
} => {
  const items: Array<{id: string; label: string}> = [];
  const disabledItems: string[] = [];
  let isSelectedCurrencyInAllowlist = false;

  for (const currency of currencyAllowlist) {
    // Filter out deprecated currencies, but keep them visible if currently selected
    // so users can see their current selection and change it if needed
    if (
      !DEPRECATED_CURRENCY_CODES.has(currency.toUpperCase()) ||
      currency.toUpperCase() === internalCurrency.toUpperCase()
    ) {
      items.push({
        id: currency,
        label: currency,
      });

      isSelectedCurrencyInAllowlist =
        isSelectedCurrencyInAllowlist || currency === internalCurrency;
    }
  }

  // If the selected currency is not in the allowlist, add it as a disabled option at the top of the list
  if (!isSelectedCurrencyInAllowlist) {
    items.unshift({
      id: internalCurrency,
      label: internalCurrency,
    });
    disabledItems.push(internalCurrency);
  }

  return {items, disabledItems};
};

export const getCurrencyFormattingMetadata = (
  currencyDigits: number,
  fractionDigitOffset: number,
): {
  denominationMultiple: number;
  fractionDigitMultiple: number;
  minimumFractionDigits: number;
  maximumFractionDigits: number;
} => {
  const fractionDigitMultiple =
    fractionDigitOffset > 0 ? 10 ** fractionDigitOffset : 1;
  const calculatedMaximumFractionDigits = currencyDigits + fractionDigitOffset;

  return {
    denominationMultiple: 10 ** currencyDigits,
    fractionDigitMultiple,
    minimumFractionDigits: currencyDigits,
    maximumFractionDigits:
      calculatedMaximumFractionDigits >= 0
        ? calculatedMaximumFractionDigits
        : 0,
  };
};

export const getEffectiveMinimumFractionDigits = (
  hasShownFractionOffset: boolean,
  denominationMultiple: number,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
): number => {
  if (hasShownFractionOffset || denominationMultiple === 1) {
    return maximumFractionDigits;
  }

  return Math.min(minimumFractionDigits, maximumFractionDigits);
};

export const stripCurrencySymbolFromFormattedValue = (
  formatted: string,
  symbol: string,
): string =>
  formatted
    .replace(symbol, '')
    .replace(/[\u00A0\u202F]/g, '')
    .trim();

export const getValuePrecisionWarning = (
  value: number | null | undefined,
  doesSupportDecimals: boolean,
  fractionDigitOffset: number,
  currency: string,
): string | null => {
  if (value == null) {
    return null;
  }

  if (!doesSupportDecimals && !Number.isInteger(value)) {
    return `'value' must be an integer for ${currency}. Received: ${value}`;
  }

  if (doesSupportDecimals) {
    const decimalStr = String(value).split('.')[1] ?? '';

    if (decimalStr.length > fractionDigitOffset) {
      return `'value' must not have more decimal places than the fractionDigitOffset allows without accuracy loss. Max: ${fractionDigitOffset}, Actual: ${decimalStr.length} (${value})`;
    }

    return null;
  }

  if (fractionDigitOffset < 0) {
    const moduloCheck = 10 ** Math.abs(fractionDigitOffset);

    if (value % moduloCheck !== 0) {
      return `'value' precision exceeds what fractionDigitOffset=${fractionDigitOffset} allows. Value ${value} is not divisible by ${moduloCheck}.`;
    }
  }

  return null;
};
